import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, ListGroup, InputGroup } from 'react-bootstrap';
import { BsSearch, BsExclamationTriangle } from 'react-icons/bs';
import ReactPaginate from 'react-paginate';
import axiosInstance from '../../utils/axiosConfig';
import toast from 'react-hot-toast';

// Helper function to ensure IDs are always strings for API calls
const ensureString = (value) => value !== undefined && value !== null ? String(value) : value;

/**
 * A comprehensive modal for managing user consent for various members (banks).
 * It can operate in two modes, controlled by the `mode` prop:
 * - 'revoke': For initiating a new revocation process from a clean slate.
 * - 'manage': For editing an existing list of selectively revoked members.
 * This single component replaces the previous `RevokeConsentModal` and `ManageRevokedBanksModal`.
 */
export const ConsentManagementModal = ({ show, handleClose, consentData, fetchData, mode }) => {
    // --- State Management ---
    const [banks, setBanks] = useState([]);
    const [selectedBanks, setSelectedBanks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [reason, setReason] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectAll, setSelectAll] = useState(false);
    const [revokeFutureEnabled, setRevokeFutureEnabled] = useState(false);
    const banksPerPage = 9;

    // --- Effects ---
    useEffect(() => {
        if (show && consentData) {
            fetchBanksList();
            // Reset state when modal opens
            setReason('');
            setCurrentPage(1);
            setSearchTerm('');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, consentData, mode]);

    // --- Data Fetching ---
    const fetchBanksList = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance().post('consent-api/getIdpList', {
                gspUserId: ensureString(consentData.id)
            });
            
            if (response.data?.code !== 200) {
                toast.error(response.data?.message || 'Failed to fetch members list');
                return;
            }

            const banksList = response.data.idpData || [];
            setBanks(banksList);

            // --- Mode-Specific Initial Selection Logic ---
            let initialSelection = [];
            const isFutureRevokeEnabled = consentData?.extra_data?.consent?.revoke_future_enabled;
            const isGloballyRevoked = consentData?.is_active === false;

            if (isGloballyRevoked) {
                // If consent is globally revoked, select all banks
                initialSelection = banksList.map(bank => ensureString(bank.idpId));
                setRevokeFutureEnabled(isFutureRevokeEnabled || false);
            } else if (mode === 'manage') {
                // In 'manage' mode, pre-select the currently revoked banks.
                const revokedBankIds = consentData?.extra_data?.consent?.revoked_banks || [];
                initialSelection = revokedBankIds.map(ensureString);
                setRevokeFutureEnabled(isFutureRevokeEnabled || false);
            } else { // 'revoke' mode
                // In 'revoke' mode, if any banks are already revoked, we start by selecting all of them.
                // This matches the logic of the original `RevokeConsentModal`.
                const hasRevokedBanks = consentData?.extra_data?.consent?.revoked_banks?.length > 0;
                if (hasRevokedBanks) {
                    initialSelection = banksList.map(bank => ensureString(bank.idpId));
                }
                setRevokeFutureEnabled(isFutureRevokeEnabled || false);
            }
            
            setSelectedBanks(initialSelection);
            setSelectAll(banksList.length > 0 && initialSelection.length === banksList.length);

        } catch (error) {
            console.error('Error fetching members list:', error);
            toast.error('Failed to fetch members list');
        } finally {
            setIsLoading(false);
        }
    };

    // --- Event Handlers ---
    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        setSelectedBanks(checked ? banks.map(bank => ensureString(bank.idpId)) : []);
    };

    const handleBankSelection = (bankId) => {
        const bankIdString = ensureString(bankId);
        
        // Update the regular selection
        const newSelection = selectedBanks.includes(bankIdString)
            ? selectedBanks.filter(id => id !== bankIdString)
            : [...selectedBanks, bankIdString];
        
        setSelectedBanks(newSelection);
        setSelectAll(banks.length > 0 && newSelection.length === banks.length);
    };
    
    const handlePageChange = (data) => {
        setCurrentPage(data.selected + 1);
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            let response;
            const payloadBase = { 
                gspUserId: ensureString(consentData.id), 
                timestamp: new Date().toISOString(),
                revokeFutureEnabled: revokeFutureEnabled
            };
            
            if (selectedBanks.length === banks.length && banks.length > 0) {
                // Global Revoke: All members are selected.
                response = await axiosInstance().post('consent-api/revokeGspUserConsent', { ...payloadBase, scope: 'global', reason });
            } else if (selectedBanks.length === 0) {
                // Global Un-revoke: No members are selected, so unrevoke all.
                response = await axiosInstance().post('consent-api/unrevokeGspUserConsent', { ...payloadBase, scope: 'global' });
            } else {
                // Selective Revoke: A subset of members is selected.
                
                // Determine which banks to unrevoke (those that were previously revoked but now unchecked)
                const previouslyRevoked = [
                    ...(consentData?.extra_data?.consent?.revoked_banks || [])
                ].map(id => ensureString(id));
                
                const currentlySelected = selectedBanks.map(id => ensureString(id));
                
                // Banks that were previously revoked but are now unchecked
                const banksToUnrevoke = previouslyRevoked.filter(id => !currentlySelected.includes(id));
                
                if (banksToUnrevoke.length > 0) {
                    // If we have banks to unrevoke, call the unrevoke endpoint first
                    await axiosInstance().post('consent-api/unrevokeGspUserConsent', { 
                        ...payloadBase, 
                        scope: 'selective', 
                        unrevokedBanks: banksToUnrevoke 
                    });
                }
                
                // Then call the revoke endpoint for the currently selected banks
                response = await axiosInstance().post('consent-api/revokeGspUserConsent', { 
                    ...payloadBase, 
                    scope: 'selective', 
                    revokedBanks: selectedBanks, 
                    reason 
                });
            }

            if (response.data?.code === 200) {
                toast.success('Consent status updated successfully');
                fetchData(); // Refresh parent component's data
                handleClose();
            } else {
                toast.error(response.data?.message || 'Failed to update consent status');
            }
        } catch (error) {
            console.error('Error updating consent status:', error);
            toast.error('Failed to update consent status');
        } finally {
            setIsLoading(false);
        }
    };

    // --- Memoized Derived State for Rendering ---
    const { currentBanks, totalPages } = useMemo(() => {
        const filtered = banks.filter(bank => 
            bank.idpName.toLowerCase().includes(searchTerm.toLowerCase())
        );
        // Sort to show selected, currently revoked, and future revoked items first
        const sorted = [...filtered].sort((a, b) => {
            const aSelected = selectedBanks.includes(ensureString(a.idpId));
            const bSelected = selectedBanks.includes(ensureString(b.idpId));
            if (aSelected !== bSelected) return aSelected ? -1 : 1;
            
            const aRevoked = consentData?.extra_data?.consent?.revoked_banks?.includes(a.idpId);
            const bRevoked = consentData?.extra_data?.consent?.revoked_banks?.includes(b.idpId);
            if (aRevoked !== bRevoked) return aRevoked ? -1 : 1;
            
            return a.idpName.localeCompare(b.idpName);
        });

        const pages = Math.ceil(sorted.length / banksPerPage);
        const paginated = sorted.slice((currentPage - 1) * banksPerPage, currentPage * banksPerPage);
        
        return { currentBanks: paginated, totalPages: pages };
    }, [banks, searchTerm, selectedBanks, currentPage, consentData]);

    // --- Dynamic UI Content based on Mode ---
    const modalTitle = mode === 'manage' ? 'Manage Revoked Members' : 'Manage Consent';
        const description = (
    <>
        {/* Instructions */}
        {mode === "manage" ? (
        <p>
            Keep members <strong>checked</strong> to continue revoking their consent.  
            Uncheck a member to <strong>restore consent</strong>.
        </p>
        ) : (
        <p>
            Select members to <strong>revoke consent</strong>.  
            Deselect to <strong>restore consent</strong>.
        </p>
        )}
        {/* Future Members Setting */}
        <small className="text-muted">
        Auto-revoke consent for new members setting determines whether new members will have consent
        revoked automatically.
        </small>
    </>
    );

    const submitButtonText = useMemo(() => {
        if (isLoading) return 'Processing...';
        return 'Update Members';
    }, [isLoading]);

    // --- Render Logic ---
    const renderPagination = () => {
        if (totalPages <= 1) return null;

        return (
            <ReactPaginate
                previousLabel={'Previous'}
                nextLabel={'Next'}
                breakLabel={'...'}
                pageCount={totalPages}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={handlePageChange}
                containerClassName={'pagination justify-content-center mt-3'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
                forcePage={currentPage - 1}
            />
        );
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted mb-3">{description}</p>
                {isLoading ? (
                    <p>Loading members...</p>
                ) : banks.length > 0 ? (
                    <>
                        <InputGroup className="mb-3">
                            <InputGroup.Text><BsSearch /></InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search members..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            />
                        </InputGroup>
                        
                        <div className="d-flex justify-content-end align-items-center mb-3">
                            <Form.Check
                                type="checkbox"
                                id="select-all-banks"
                                label="Select All Members"
                                checked={selectAll}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className={revokeFutureEnabled ? "me-3" : ""}
                            />
                            {revokeFutureEnabled && (
                                <div className="d-flex align-items-center">
                                    <span className="text-muted small me-2">Future Members:</span>
                                    <span className="badge bg-warning text-dark">Auto-Revoke ON</span>
                                </div>
                            )}
                        </div>
                        
                        <ListGroup className="mb-3">
                            {currentBanks.map(bank => {
                                const bankIdString = ensureString(bank.idpId);
                                
                                // Check revocation state
                                const isCurrentlyRevoked = consentData?.extra_data?.consent?.revoked_banks?.some(
                                    revokedId => ensureString(revokedId) === bankIdString
                                );
                                
                                return (
                                    <ListGroup.Item key={bank.idpId} className="d-flex align-items-center justify-content-between">
                                        <Form.Check
                                            type="checkbox"
                                            id={`bank-${bank.idpId}`}
                                            label={bank.idpName}
                                            checked={selectedBanks.includes(bankIdString)}
                                            onChange={() => handleBankSelection(bank.idpId)}
                                        />
                                        <div className="d-flex">
                                            {isCurrentlyRevoked && (
                                                <span className="badge bg-danger me-1">Currently Revoked</span>
                                            )}
                                        </div>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                        {renderPagination()}
                    </>
                ) : searchTerm ? (
                    <p>No members found matching "{searchTerm}".</p>
                ) : (
                    <p>No members found for this consent.</p>
                )}
                {/* Enhanced Future Revoke Section */}
                <Form.Group className="mt-3">
                    <Form.Check
                        type="checkbox"
                        id="revoke-future-toggle"
                        label="Auto-revoke consent for new members"
                        checked={revokeFutureEnabled}
                        onChange={(e) => setRevokeFutureEnabled(e.target.checked)}
                    />
                    
                    {revokeFutureEnabled && (
                        <div className="mt-2 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                            <div className="d-flex align-items-center">
                                <BsExclamationTriangle className="text-warning me-2" />
                                <div>
                                    <strong>Future Revoke Active</strong>
                                    <br />
                                    <small className="text-muted">
                                        All new members will be automatically revoked. 
                                        To allow specific members, disable future revoke first.
                                    </small>
                                </div>
                            </div>
                        </div>
                    )}
                    

                </Form.Group>

                <Form.Group className="mt-3">
                    <Form.Label>Reason for Changes (Optional)</Form.Label>
                    <Form.Control as="textarea" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Enter reason for changes" />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isLoading}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>{submitButtonText}</Button>
            </Modal.Footer>
        </Modal>
    );
};


















